import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import DataTable from "../../../../components/customTable";
import TableForm from "../../../../components/DepartmentTableForm";
import ModalManager from "../../../../components/modalManager";
import styles from "./department.module.css";
import { Button } from 'antd';
import apiCall from "../../../API/apiService";
import apiEndpoints from "../../../API/apiEndpoints";
import toast from 'react-hot-toast';
import { Pagination } from 'antd';
import Typography from '@mui/material/Typography'; // Add this import
import { useDispatch } from "react-redux";
import { setOpenModal } from "../../../../redux/Actions/UserAction";
import {CreateDepartment} from "../../../assets/svgComponent"; // Ensure this path is correct

const Department = () => {
	const [rows, setRows] = useState([]);
	const [organizations, setOrganizations] = useState([]);
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(15);
	const [editingIndex, setEditingIndex] = useState(null);
	const [newItem, setNewItem] = useState({
		id: "",
		name: "",
		description: "",
		head: "",
		no_subdepartments: "",
		
		organisation_id: "",
		organisation_name: "",
	});

	const dispatch = useDispatch();

	useEffect(() => {
		// Only fetch departments if we don't have data for the current page
		if (rows.length === 0) {
			fetchData();
		}
	}, [page, rowsPerPage]); // Dependencies array includes page and rowsPerPage

	// When page changes, check if we need to fetch new data
	useEffect(() => {
		// If we don't have data for the current page, fetch it
		const startIndex = (page - 1) * rowsPerPage;
		const endIndex = startIndex + rowsPerPage;
		
		// Check if we need to fetch more data
		if (rows.length < endIndex && totalItems > rows.length) {
			fetchData();
		}
	}, [page, rowsPerPage]);

	const toasty = (message, type) => {
		if (type === "error") {
			toast.error(message, {
				style: {
					border: '1px solid #2c5da7',
					padding: '16px',
					color: '#2c5da7',
				},
				iconTheme: {
					primary: '#2c5da7',
					secondary: '#FFFAEE',
				},
			});
		} else {
			toast.success(message, {
				style: {
					border: '1px solid #2c5da7',
					padding: '16px',
					color: '#2c5da7',
				},
				iconTheme: {
					primary: '#2c5da7',
					secondary: '#FFFAEE',
				},
			});
		}
	};

	const fetchOrganizations = async () => {
		try {
			const response = await apiCall("get", apiEndpoints.ORGANIZATION_SUMMARY);
			
			if (response && Array.isArray(response)) {
				const orgs = response.map(org => ({
					id: org.id,
					name: org.name
				}));
				setOrganizations(orgs);
			} else {
				console.error("Invalid organization data format:", response);
			}
		} catch (error) {
			console.error("Error fetching organizations:", error);
			toasty("Failed to fetch organizations", "error");
		}
	};

	const fetchData = async () => {
		if (isLoading) return; // Prevent multiple simultaneous requests
		
		setIsLoading(true);
		try {
			const data = await apiCall("get", `${apiEndpoints.DEPARTMENT}?page=${page}&page_size=${rowsPerPage}`);
			console.log("Fetched Data:", data); // Log the fetched data
			if (Array.isArray(data.items)) {
				setRows(data.items);
				setTotalItems(data.total);
				setTotalPages(Math.ceil(data.total / rowsPerPage));
			} else {
				console.error("Invalid data format:", data);
				toasty("Failed to fetch departments", "error");
			}
		} catch (error) {
			console.error("Error fetching departments:", error);
			toasty("Failed to fetch departments", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const postData = async (obj) => {
		try {
			const data = await apiCall("post", apiEndpoints.DEPARTMENT, obj);
			setRows(prevRows => [...prevRows, data]);
			toasty("Department Created successfully");
		} catch (error) {
			console.error("Error creating department:", error);
			toasty("Failed to create department", "error");
		}
	};

	const putData = async (obj) => {
		try {
			const data = await apiCall("put", apiEndpoints.DEPARTMENT, obj);
			setRows(prevRows => prevRows.map(item => item.id === data.id ? data : item));
			toasty('Department Updated successfully');
		} catch (error) {
			console.error("Error updating department:", error);
			toasty("Failed to update department", "error");
		}
	};

	const deleteData = async (id) => {
		try {
			await apiCall("delete", `${apiEndpoints.DEPARTMENT}/${id}`);
			setRows(prevRows => prevRows.filter(item => item.id !== id));
			toasty("Department Deleted successfully", "error");
		} catch (error) {
			console.error("Error deleting department:", error);
			toasty("Failed to delete department", "error");
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setNewItem(prevItem => {
			if (name === 'organisation_id') {
				const selectedOrg = organizations.find(org => org.id === value);
				return {
					...prevItem,
					[name]: value,
					organisation_name: selectedOrg ? selectedOrg.name : ''
				};
			}
			return { ...prevItem, [name]: value };
		});
	};

	const handleAddRow = async () => {
		const raw = {
			name: newItem.name,
			description: newItem.description,
			head: newItem.head,
			organisation_id: newItem.organisation_id,
		};
		await postData(raw);
		setNewItem({
			id: "",
			name: "",
			description: "",
			head: "",
			no_subdepartments: "",
			organisation_id: "",
			organisation_name: "", // Reset organization name as well
		});
	};

	const handleSaveRow = async () => {
		const raw = {
			id: newItem.id,
			name: newItem.name,
			description: newItem.description,
			head: newItem.head,
			organisation_id: newItem.organisation_id,
		};
		await putData(raw);
		await fetchData();
		setEditingIndex(null);
		setNewItem({
			id: "",
			name: "",
			description: "",
			head: "",
			no_subdepartments: "",
			organisation_id: "",
			organisation_name: "", // Reset organization name as well
		});
	};

	const handleEditRow = (id) => {
		const itemToEdit = rows.find((row) => row.id === id);
		if (itemToEdit) {
			// Fetch organizations when opening the edit modal
			fetchOrganizations().then(() => {
				setNewItem({
					...itemToEdit,
					organisation_name: organizations.find(org => org.id === itemToEdit.organisation_id)?.name || ''
				});
				setEditingIndex(id); // Set the editing index to the current item
				dispatch(setOpenModal({ modalVisible: true })); // Open the modal for editing
			});
		}
	};

	const handleDeleteRow = async (id) => {
		await deleteData(id);
		await fetchData();
	};

	const handleSelectChange = (e, id) => {
		const updatedDepartment = rows.find(row => row.id === id);
		if (updatedDepartment) {
			setRows(prevRows => prevRows.map(item => item.id === updatedDepartment.id ? { ...item, status: e.target.value } : item));
		}
	};

	// Open the modal for creating a new department
	const handleOpenCreateModal = async () => {
		setIsLoading(true); // Set loading state to true
		try {
			await fetchOrganizations(); // Fetch organizations
			setNewItem({
				id: "",
				name: "",
				description: "",
				head: "",
				no_subdepartments: "",
				organisation_id: "",
				organisation_name: "",
			});
			setEditingIndex(null);
			dispatch(setOpenModal({ modalVisible: true }));
		} catch (error) {
			console.error("Error fetching organizations:", error);
			toasty("Failed to fetch organizations", "error");
		} finally {
			setIsLoading(false); // Reset loading state
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage); // Update page state
	};

	return (
		<div className={styles.container}>
			{/* Create Department Button */}
			<div className={styles.buttonContainer}>
				<span>List of Department</span>
				<img 
					style={{
						cursor: 'pointer',
						width: '155px', // Set width
						height: '32px', // Set height
						padding: '2px 10px 2px 8px', // Set padding
						gap: '6px', // Set gap (note: gap is typically used in flex containers)
						borderRadius: '6px', // Set border radius
						
					}} 
					onClick={handleOpenCreateModal} 
					src={CreateDepartment}
				/>
			</div>

			<ModalManager customWidth={window.innerWidth/300}>
				<TableForm
					item={newItem}
					onChange={handleInputChange}
					onSave={editingIndex !== null ? handleSaveRow : handleAddRow}
					onClear={() => setNewItem({
						id: "",
						name: "",
						description: "",
						head: "",
						no_subdepartments: "",
						organisation_id: "",
						organisation_name: "",
					})}
					organizations={organizations || []}
					isEditing={editingIndex !== null}
				/>
			</ModalManager>
         

				 
		
					{/* DataTable for displaying departments */}
					<DataTable
						rows={rows}
						handleSelectChange={handleSelectChange}
						handleEditRow={handleEditRow}
						handleDeleteRow={handleDeleteRow}
						organizations={organizations}
						isEditing={true}
					/>

					{/* Pagination Component */}
					
		</div>
	);
};

export default Department;
